import { useEffect, useState, useMemo } from "react";
import api, { formatIDR, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import SearchBar from "@/components/SearchBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash, ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import RupiahInput from "@/components/RupiahInput";

export default function Purchases() {
  const [items, setItems] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    supplier_name: "", supplier_phone: "", supplier_address: "",
    purchase_date: new Date().toISOString().slice(0, 10),
    brand: "", model: "", type: "", year: new Date().getFullYear(), plate_number: "", color: "",
    account_number: "",
    purchase_price: "", selling_price: "",
    makelar_name: "", makelar_commission: "0",
    notes: "",
  });

  const load = () => api.get("/purchases").then((r) => setItems(r.data));
  const loadCatalog = () => api.get("/car-catalog").then((r) => setCatalog(r.data.grouped || {}));
  useEffect(() => { load(); loadCatalog(); }, []);

  const brands = useMemo(() => Object.keys(catalog).sort(), [catalog]);
  const models = useMemo(() => (catalog[form.brand] ? Object.keys(catalog[form.brand]).sort() : []), [catalog, form.brand]);
  const types = useMemo(() => (catalog[form.brand]?.[form.model] || []), [catalog, form.brand, form.model]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) =>
      (it.supplier_name || "").toLowerCase().includes(s) ||
      (it.supplier_phone || "").toLowerCase().includes(s) ||
      (it.car?.brand || "").toLowerCase().includes(s) ||
      (it.car?.model || "").toLowerCase().includes(s) ||
      (it.car?.plate_number || "").toLowerCase().includes(s)
    );
  }, [items, q]);

  const submit = async () => {
    try {
      const payload = {
        ...form,
        year: parseInt(form.year),
        purchase_price: parseFloat(form.purchase_price),
        selling_price: parseFloat(form.selling_price),
        makelar_commission: parseFloat(form.makelar_commission || 0),
      };
      await api.post("/purchases", payload);
      toast.success("Pembelian dicatat, mobil masuk inventori");
      setOpen(false);
      setForm({ ...form, supplier_name: "", brand: "", model: "", type: "", plate_number: "", color: "", purchase_price: "", selling_price: "", notes: "" });
      load(); loadCatalog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus data pembelian? Mobil yang belum terjual juga akan terhapus dari inventori.")) return;
    await api.delete(`/purchases/${id}`);
    toast.success("Terhapus");
    load();
  };

  return (
    <div>
      <PageHeader title="Pembelian Mobil" subtitle="Catat mobil yang dibeli showroom dari supplier / pemilik"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-purchase-btn" className="rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-10">
                <Plus size={14} className="mr-2" /> Beli Mobil
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">Pembelian Mobil</DialogTitle></DialogHeader>
              <div className="pt-2 space-y-6">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 font-medium mb-3">Data Penjual / Supplier</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider">Nama Supplier</Label><Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} className="mt-2 rounded-none" data-testid="purchase-supplier-name" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Telepon</Label><Input value={form.supplier_phone} onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })} className="mt-2 rounded-none font-mono-num" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Tanggal Beli</Label><Input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="mt-2 rounded-none" data-testid="purchase-date" /></div>
                    <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider">Alamat</Label><Input value={form.supplier_address} onChange={(e) => setForm({ ...form, supplier_address: e.target.value })} className="mt-2 rounded-none" /></div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 font-medium mb-3">Data Mobil</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Merek</Label>
                      <Input list="pur-brands" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value, model: "", type: "" })} className="mt-2 rounded-none" data-testid="purchase-brand" placeholder="Pilih atau ketik" />
                      <datalist id="pur-brands">{brands.map((b) => <option key={b} value={b} />)}</datalist>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wider">Model</Label>
                      <Input list="pur-models" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value, type: "" })} className="mt-2 rounded-none" data-testid="purchase-model" placeholder="Pilih atau ketik" />
                      <datalist id="pur-models">{models.map((m) => <option key={m} value={m} />)}</datalist>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs uppercase tracking-wider">Type / Varian</Label>
                      <Input list="pur-types" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="mt-2 rounded-none" data-testid="purchase-type" placeholder="Contoh: Veloz 1.5 AT" />
                      <datalist id="pur-types">{types.map((t) => <option key={t} value={t} />)}</datalist>
                    </div>
                    <div><Label className="text-xs uppercase tracking-wider">Tahun</Label><Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="mt-2 rounded-none font-mono-num" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Plat</Label><Input value={form.plate_number} onChange={(e) => setForm({ ...form, plate_number: e.target.value })} className="mt-2 rounded-none font-mono-num" /></div>
                    <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider">Warna</Label><Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="mt-2 rounded-none" /></div>
                    <div className="md:col-span-2"><Label className="text-xs uppercase tracking-wider">No. Rekening <span className="text-gray-400 normal-case">(manual)</span></Label><Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="Contoh: 1501" className="mt-2 rounded-none font-mono-num" data-testid="purchase-account" /></div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 font-medium mb-3">Makelar (opsional)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider">Nama Makelar</Label><Input value={form.makelar_name} onChange={(e) => setForm({ ...form, makelar_name: e.target.value })} placeholder="Kosongkan jika tanpa makelar" className="mt-2 rounded-none" data-testid="purchase-makelar" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Komisi Makelar</Label><RupiahInput value={form.makelar_commission} onChange={(e) => setForm({ ...form, makelar_commission: e.target.value })} className="mt-2 rounded-none" data-testid="purchase-commission" /></div>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-gray-500 font-medium mb-3">Harga</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><Label className="text-xs uppercase tracking-wider">Harga Beli</Label><RupiahInput value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} className="mt-2 rounded-none" data-testid="purchase-cost" /></div>
                    <div><Label className="text-xs uppercase tracking-wider">Rencana Jual</Label><RupiahInput value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} className="mt-2 rounded-none" data-testid="purchase-selling" /></div>
                  </div>
                </div>
                <div><Label className="text-xs uppercase tracking-wider">Catatan</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-none" /></div>
                <Button onClick={submit} data-testid="purchase-submit" className="w-full rounded-none bg-[#0A0A0A] hover:bg-gray-800 text-white uppercase tracking-wider text-xs h-11">Simpan Pembelian</Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-4 md:p-6 lg:p-10">
        <SearchBar value={q} onChange={setQ} placeholder="Cari supplier / mobil / plat..." testid="purchases-search" count={filtered.length} />
        <div className="border border-gray-200 overflow-x-auto overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-gray-600">
                <th className="p-4">Tanggal</th>
                <th className="p-4">Supplier</th>
                <th className="p-4">Mobil</th>
                <th className="p-4">Status</th>
                <th className="p-4 text-right">Harga Beli</th>
                <th className="p-4 text-right">Rencana Jual</th>
                <th className="p-4 text-right">Margin</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const margin = (p.selling_price || 0) - (p.purchase_price || 0);
                return (
                  <tr key={p.id} className="border-b border-gray-100" data-testid={`purchase-row-${p.id}`}>
                    <td className="p-4 font-mono-num text-xs">{formatDate(p.purchase_date)}</td>
                    <td className="p-4">
                      <div className="font-semibold">{p.supplier_name}</div>
                      <div className="text-xs text-gray-500 font-mono-num">{p.supplier_phone}</div>
                    </td>
                    <td className="p-4">
                      <div className="font-semibold">{p.car?.brand} {p.car?.model}</div>
                      <div className="text-xs text-gray-500">{p.car?.type ? p.car.type + " · " : ""}{p.car?.year} · {p.car?.plate_number}</div>
                    </td>
                    <td className="p-4">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${p.car?.status === "sold" ? "bg-gray-900 text-white" : "border border-gray-900"}`}>
                        {p.car?.status === "sold" ? "Terjual" : "Stok"}
                      </span>
                    </td>
                    <td className="p-4 text-right font-mono-num">{formatIDR(p.purchase_price)}</td>
                    <td className="p-4 text-right font-mono-num">{formatIDR(p.selling_price)}</td>
                    <td className="p-4 text-right font-mono-num font-semibold" style={{ color: margin >= 0 ? "#10B981" : "#E63946" }}>
                      {formatIDR(margin)}
                    </td>
                    <td className="p-4 text-right whitespace-nowrap">
                      <Link to={`/purchases/${p.id}`} className="mr-3 inline-flex items-center gap-1 text-xs uppercase tracking-wider hover:underline" data-testid={`purchase-view-${p.id}`}>
                        Detail <ArrowRight size={12} />
                      </Link>
                      <button onClick={() => del(p.id)} className="text-gray-500 hover:text-[#E63946]" data-testid={`purchase-del-${p.id}`}><Trash size={16} /></button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan="8" className="p-12 text-center text-sm text-gray-500">{items.length === 0 ? "Belum ada pembelian" : "Tidak ada hasil"}</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
